// The embed loader. Runs on the host (customer) page. Creates a launcher
// bubble in a shadow root (so host page CSS can't bleed into it, and vice
// versa) — a silent, looping video of an agent talking, either the one this
// bundle ships (widget/assets/) or the customer's own via data-video. The
// visitor can drag it anywhere on the screen (remembered per bot in
// localStorage), and a click opens the chat panel in an iframe, placed next
// to wherever the launcher is, for real CSS/JS isolation. Talks to the
// panel exclusively via postMessage with strict origin checks on both
// sides — see PHASE-1-NOTES.md for the trust model.
//
// This file also owns fetching the session token (see
// widget/src/shared/protocol.ts for why it has to be the loader, not the
// panel) and is wrapped defensively throughout: nothing here should ever
// throw into the host page's own console or interrupt its JS.

import type { LoaderToPanelMessage, PanelToLoaderMessage, SessionFailure } from '../shared/protocol';

/** Content-hashed paths of the launcher's default video, relative to the
 * backend origin, injected at build time by widget/esbuild.config.mjs —
 * one <source> each, in order. See widget/assets/README.md. */
declare const __CW_LAUNCHER_MEDIA__: string[];
/** Every launcher file this build ships, 'v1/<slug>.<hash>.<ext>' — the
 * gallery the dashboard picks from, injected the same way. */
declare const __CW_LAUNCHER_FILES__: string[];

/**
 * A bundled launcher's current file. The dashboard stores the hashed path
 * of the launcher a customer picked (/v1/<slug>.<hash>.webm), and a
 * re-encoded source file gets a new hash on the next deploy: the stored
 * path then 404s and the bot quietly shows the generic drawn agent. The
 * slug is stable, so the file this build has for it is used instead. Any
 * other value — a customer's own URL, an upload — is returned as it is.
 */
function currentBuiltInLauncher(stored: string | undefined): string | undefined {
  if (!stored) return stored;
  const match = /^\/(v1\/[a-z0-9-]+)\.[0-9a-f]{10}\.(webm|mp4|gif|webp|png|apng|jpe?g|svg)$/i.exec(stored);
  if (!match) return stored;
  const files = typeof __CW_LAUNCHER_FILES__ !== 'undefined' ? __CW_LAUNCHER_FILES__ : [];
  if (files.includes(stored.slice(1))) return stored;
  const current = files.find((file) => file.startsWith(`${match[1]}.`) && /^\.[0-9a-f]{10}\.[a-z0-9]+$/i.test(file.slice(match[1].length)));
  return current ? `/${current}` : stored;
}

interface ChatWidgetPublicApi {
  open(): void;
  close(): void;
  toggle(): void;
  /** Shows the chat invitation beside the launcher again. */
  invite(): void;
}

/** The bot's look as currently set in the dashboard, carried on the
 * session response (server/src/http/routes/session.ts). */
interface ServerTheme {
  accent: string | null;
  /** null means "no logo" — the built-in agent. */
  logoUrl: string | null;
}

interface SessionInfo {
  token: string | null;
  expiresAt: number | null;
  failure: SessionFailure | null;
  /** Null when the server didn't say (a failed request) — the snippet's own
   * attributes stay in charge then. */
  theme: ServerTheme | null;
  /** Show the chat invitation beside the launcher (the bot's Interactions
   * tab; on for sales demos). False when the server didn't say. */
  callout: boolean;
}

/** Reads the theme off a session response, keeping only values the
 * dashboard itself would have accepted: a 6-digit hex colour, and an
 * https: or data:image/ logo. Anything else is treated as unset rather
 * than painted onto the customer's page. */
function parseServerTheme(raw: unknown): ServerTheme | null {
  if (!raw || typeof raw !== 'object') return null;
  const { accent, logoUrl } = raw as { accent?: unknown; logoUrl?: unknown };
  return {
    accent: typeof accent === 'string' && /^#[0-9a-f]{6}$/i.test(accent) ? accent : null,
    logoUrl:
      typeof logoUrl === 'string' && /^(https:\/\/|data:image\/|\/v1\/)/i.test(logoUrl) ? logoUrl : null,
  };
}

// Tokens last 15 minutes (server/src/security/session.ts). Renewing this
// far ahead means an open panel never holds an expired token, and a
// laptop back from sleep gets a fresh one on the visibility change,
// before the visitor can type.
const RENEW_BEFORE_MS = 2 * 60 * 1000;
// The first session check gates the launcher, but a slow network mustn't
// hide the chat — show it after this long regardless.
const REVEAL_TIMEOUT_MS = 2000;
const SESSION_REQUEST_TIMEOUT_MS = 8000;

// Moving the launcher. A press that travels less than the threshold is a
// click (opens the chat); more is a drag. Nothing ever lands closer to the
// viewport edge than the margin, so a dragged launcher can't be lost
// off-screen — and neither can the panel that follows it.
const DRAG_THRESHOLD_PX = 6;
const EDGE_MARGIN_PX = 8;
const PANEL_GAP_PX = 12;
// Below this width the stylesheet takes the panel full-screen (see
// LAUNCHER_CSS), so there's no launcher-relative placement to do.
const MOBILE_MAX_WIDTH_PX = 640;
// A minimized panel is its header bar, measured by the panel itself (about
// 52px). The bounds only stop a bad measurement from sizing the frame into
// a sliver or a slab; the fallback covers a message with no number at all.
const MINIMIZED_MIN_HEIGHT_PX = 40;
const MINIMIZED_MAX_HEIGHT_PX = 120;
const MINIMIZED_FALLBACK_HEIGHT_PX = 52;
// The launcher's one-time nudge: how long after it appears, and for how long.
const NUDGE_DELAY_MS = 6000;
const NUDGE_SHOW_MS = 7000;
// The chat invitation: how long after the launcher appears it arrives,
// and how long its "typing" dots run before the message shows.
const CALLOUT_DELAY_MS = 1200;
const CALLOUT_TYPING_MS = 1300;

const CLOSE_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const SPARKLE_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="currentColor">' +
  '<path d="M12 2.5l1.9 5.1 5.1 1.9-5.1 1.9L12 16.5l-1.9-5.1L5 9.5l5.1-1.9z"/><path d="M18.5 14l.9 2.4 2.4.9-2.4.9-.9 2.4-.9-2.4-2.4-.9 2.4-.9z" opacity="0.85"/></svg>';
const CALLOUT_HTML =
  '<span class="cw-callout-tail" aria-hidden="true"></span>' +
  '<div class="cw-callout-card">' +
  `<button type="button" class="cw-callout-close" aria-label="Close this message">${CLOSE_ICON_SVG}</button>` +
  `<div class="cw-callout-head"><span class="cw-callout-avatar" aria-hidden="true">${SPARKLE_ICON_SVG}</span>` +
  '<span class="cw-callout-who"><strong>Hi there! <span class="cw-wave" aria-hidden="true">👋</span></strong>' +
  '<span class="cw-callout-status"><i aria-hidden="true"></i>AI assistant · online now</span></span></div>' +
  '<div class="cw-callout-typing" aria-hidden="true"><i></i><i></i><i></i></div>' +
  '<p class="cw-callout-msg">Got a question? Ask me anything and get an answer in seconds, any time of day.</p>' +
  '<button type="button" class="cw-callout-cta">Chat with our AI assistant<span class="cw-callout-arrow" aria-hidden="true">→</span></button>' +
  '</div>';
// The open panel is never shorter than this on a desktop, whatever the
// panel reports or the window allows — expanded or not.
const PANEL_MIN_HEIGHT_PX = 320;
// The header's expand button: one larger, fixed size for the open panel on
// a desktop — room for long answers and forms — fitted to a smaller window
// rather than hanging off it. A phone's panel is already the whole screen,
// so there is nothing to expand there.
const EXPANDED_WIDTH_PX = 640;
const EXPANDED_HEIGHT_PX = 800;

// The launcher's drawn stand-in: a looping, silent animation of a support
// agent talking (mouth, blink and head-bob driven by the CSS below). Inline
// SVG, so unlike a media file it can't 404, costs no request, and needs no
// `media-src`/`img-src` in a strict host CSP. It's what a visitor sees when
// the real video can't play — no WebM support (Safari before 16, iOS before
// 17.4), a blocked or failed request, a customer's own `data-video` that
// 404s — so the bubble is never an empty circle.
const AGENT_VIDEO_SVG =
  '<svg class="cw-agent" viewBox="7 7 50 50" width="60" height="60" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
  '<circle cx="32" cy="32" r="32" fill="#ffffff" fill-opacity="0.16"/>' +
  // shoulders + collar, drawn first so the head sits on top of them
  '<path d="M8 64c1.6-10.9 11.2-16 24-16s22.4 5.1 24 16z" fill="#28374f"/>' +
  '<path d="M27 48.6 32 55l5-6.4" fill="none" stroke="#1b2536" stroke-width="1.7" stroke-linejoin="round"/>' +
  '<path d="M28 39.5h8v9h-8z" fill="#dfa87d"/>' + // neck
  '<g class="cw-agent-head">' +
  '<ellipse cx="19" cy="29" rx="2.6" ry="3.1" fill="#f2c6a0"/>' + // ears
  '<ellipse cx="45" cy="29" rx="2.6" ry="3.1" fill="#f2c6a0"/>' +
  '<ellipse cx="32" cy="28" rx="13" ry="14.5" fill="#f2c6a0"/>' + // face
  '<path d="M19 27c0-8.3 5.8-13.6 13-13.6S45 18.7 45 27c-1.9-4.5-5.7-6.5-13-6.5S20.9 22.5 19 27z" fill="#3b2b25"/>' + // hair
  '<path d="M23.4 23.6c1.8-1.1 4-1.1 5.7 0" fill="none" stroke="#3b2b25" stroke-width="1.5" stroke-linecap="round"/>' + // brows
  '<path d="M34.9 23.6c1.7-1.1 3.9-1.1 5.7 0" fill="none" stroke="#3b2b25" stroke-width="1.5" stroke-linecap="round"/>' +
  '<g class="cw-agent-eye"><ellipse cx="26.4" cy="28" rx="1.9" ry="2.3" fill="#2b2118"/></g>' +
  '<g class="cw-agent-eye"><ellipse cx="37.6" cy="28" rx="1.9" ry="2.3" fill="#2b2118"/></g>' +
  '<path d="M32 28.8v3" fill="none" stroke="#d99f77" stroke-width="1.6" stroke-linecap="round"/>' + // nose
  '<g class="cw-agent-mouth">' +
  '<ellipse cx="32" cy="36" rx="3.7" ry="2.7" fill="#8d4a46"/>' +
  '<path d="M28.7 34.8h6.6" fill="none" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round" opacity="0.9"/>' + // teeth
  '</g>' +
  // headset: band, ear cup and mic boom — the bit that reads as "agent"
  '<path d="M17.6 29a14.4 14.4 0 0 1 28.8 0" fill="none" stroke="#111827" stroke-width="2.6" stroke-linecap="round"/>' +
  '<rect x="14.2" y="25.6" width="5.4" height="8.4" rx="2.7" fill="#111827"/>' +
  '<rect x="44.4" y="25.6" width="5.4" height="8.4" rx="2.7" fill="#111827"/>' +
  '<path d="M47.1 34c0 5.2-4.2 9-9.1 9.9" fill="none" stroke="#111827" stroke-width="1.7" stroke-linecap="round"/>' +
  '<circle cx="37.4" cy="44.1" r="1.7" fill="#111827"/>' +
  '</g>' +
  '</svg>';

// File extensions we hand to a <video> element; anything else (.gif, .webp,
// .apng, .svg, a still logo) is an <img>. Both are muted by construction —
// <img> has no audio track at all, and the <video> below is force-muted.
const VIDEO_URL_PATTERN = /\.(mp4|webm|ogv|ogg|mov|m4v)(?:[?#]|$)/i;

// The size toggle's two faces: arrows pointing in (make it smaller) and
// out (make it larger again).
const SHRINK_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"/></svg>';
const EXPAND_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>';

const LAUNCHER_CSS = `
  /* Holds the launcher and its size toggle, and is what gets positioned and
     dragged — the toggle can't live inside the launcher <button> itself. */
  #cw-launcher-wrap {
    position: fixed;
    bottom: 20px;
    right: 20px;
    /* Big enough that a face — and the fact that it's talking — reads at a
       glance. The video is 256px, so this is crisp on 2x displays. */
    width: 128px;
    height: 128px;
    z-index: 2147483000;
  }
  /* The visitor's "small" choice: half the size, both axes. */
  #cw-launcher-wrap.cw-small {
    width: 64px;
    height: 64px;
  }
  @media (max-width: 640px) {
    #cw-launcher-wrap {
      width: 88px;
      height: 88px;
    }
    #cw-launcher-wrap.cw-small {
      width: 44px;
      height: 44px;
    }
  }
  #cw-launcher {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: none;
    background: var(--cw-accent, #2563EB);
    box-shadow: 0 8px 24px rgba(0,0,0,0.24);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    overflow: hidden; /* clips the launcher video to the circle */
    transition: transform 0.15s ease;
    /* Draggable: the browser must not turn a touch-drag into a page scroll,
       and a long press mustn't select text or pop a callout. */
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
  }
  #cw-launcher:hover { transform: scale(1.06); }
  #cw-launcher.cw-dragging {
    cursor: grabbing;
    transform: none;
    transition: none;
  }
  #cw-launcher:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 3px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.24), 0 0 0 4px var(--cw-accent, #2563EB);
  }
  /* Large <-> small toggle on the launcher's top-left corner. Shown on
     hover or keyboard focus where there's a mouse; always on touch
     screens, which have no hover to reveal it. */
  #cw-size-toggle {
    position: absolute;
    top: -2px;
    left: -2px;
    z-index: 1;
    width: 26px;
    height: 26px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #fff;
    color: #111827;
    box-shadow: 0 2px 8px rgba(0,0,0,0.28);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s ease;
    -webkit-tap-highlight-color: transparent;
  }
  #cw-launcher-wrap:hover #cw-size-toggle,
  #cw-launcher-wrap:focus-within #cw-size-toggle {
    opacity: 1;
  }
  @media (hover: none) {
    #cw-size-toggle { opacity: 1; }
  }
  #cw-launcher-wrap.cw-small #cw-size-toggle {
    top: -6px;
    left: -6px;
    width: 22px;
    height: 22px;
  }
  #cw-size-toggle:focus-visible {
    outline: 2px solid var(--cw-accent, #2563EB);
    outline-offset: 2px;
  }
  /* The launcher's face: a <video>, an <img> (gif/webp/logo), or the
     built-in animated agent below. All three fill the circle identically. */
  .cw-launcher-media,
  .cw-agent {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    /* The click belongs to the button — without this a <video> swallows it
       (and offers its own context menu) on some browsers. */
    pointer-events: none;
  }
  .cw-agent-head {
    transform-box: fill-box;
    transform-origin: 50% 100%;
    animation: cw-agent-bob 4.2s ease-in-out infinite;
  }
  .cw-agent-eye,
  .cw-agent-mouth {
    transform-box: fill-box;
    transform-origin: center;
  }
  .cw-agent-eye { animation: cw-agent-blink 5.4s linear infinite; }
  .cw-agent-mouth { animation: cw-agent-talk 3.4s linear infinite; }
  /* Nothing to say while the visitor is reading the panel — and no reason
     to keep decoding frames behind it. The <video> is paused in JS. */
  #cw-launcher[aria-expanded='true'] .cw-agent * { animation-play-state: paused; }
  @keyframes cw-agent-bob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(-0.7px) rotate(1.4deg); }
    50% { transform: translateY(0.4px) rotate(0deg); }
    75% { transform: translateY(-0.4px) rotate(-1.4deg); }
  }
  @keyframes cw-agent-blink {
    0%, 92%, 100% { transform: scaleY(1); }
    94%, 95.5% { transform: scaleY(0.1); }
  }
  /* Speech, not a metronome: uneven syllables, then a beat of silence
     between 72% and 88% so the loop reads as sentences. */
  @keyframes cw-agent-talk {
    0% { transform: scaleY(0.25) scaleX(0.94); }
    5% { transform: scaleY(1.05) scaleX(1.02); }
    10% { transform: scaleY(0.35) scaleX(0.96); }
    16% { transform: scaleY(0.9) scaleX(1); }
    22% { transform: scaleY(0.3) scaleX(0.95); }
    28% { transform: scaleY(1.15) scaleX(1.04); }
    34% { transform: scaleY(0.4) scaleX(0.97); }
    40% { transform: scaleY(0.85) scaleX(1); }
    46% { transform: scaleY(0.25) scaleX(0.94); }
    52% { transform: scaleY(1) scaleX(1.02); }
    58% { transform: scaleY(0.35) scaleX(0.96); }
    64% { transform: scaleY(0.75) scaleX(0.99); }
    72%, 88% { transform: scaleY(0.2) scaleX(0.92); }
    94% { transform: scaleY(0.6) scaleX(0.98); }
    100% { transform: scaleY(0.25) scaleX(0.94); }
  }
  /* A looping face is exactly what reduced motion is asking us not to do:
     hold the agent still (the <video> is left paused on its first frame). */
  /* The hint beside the launcher: who it is and what clicking does. On the
     side facing the middle of the screen (cw-tip-right when the launcher
     sits on the left half), centred on it, with a little arrow pointing
     at it. Decoration for sighted mouse users — the button's own label
     already says "Open chat" — so it never takes a click. */
  #cw-tip {
    position: absolute;
    top: 50%;
    right: calc(100% + 14px);
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 14px;
    border-radius: 14px;
    background: #fff;
    color: #111827;
    box-shadow: 0 10px 30px rgba(17,24,39,0.18), 0 1px 3px rgba(17,24,39,0.12);
    font: 600 14px/1.3 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transform: translate(6px, -50%);
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  #cw-tip::after {
    content: '';
    position: absolute;
    top: 50%;
    right: -5px;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background: #fff;
    transform: translateY(-50%) rotate(45deg);
  }
  #cw-launcher-wrap.cw-tip-right #cw-tip {
    right: auto;
    left: calc(100% + 14px);
    transform: translate(-6px, -50%);
  }
  #cw-launcher-wrap.cw-tip-right #cw-tip::after { right: auto; left: -5px; }
  .cw-tip-title { display: flex; align-items: center; gap: 8px; }
  .cw-tip-dot {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    animation: cw-tip-pulse 1.8s ease-out infinite;
  }
  .cw-tip-sub { padding-left: 16px; font-weight: 400; font-size: 12.5px; color: #6b7280; }
  .cw-tip-touch { display: none; }
  @media (hover: none) {
    .cw-tip-mouse { display: none; }
    .cw-tip-touch { display: inline; }
  }
  /* Shown while a mouse is over the launcher itself (not the size toggle,
     which does something else), while it has keyboard focus, and for the
     one-time nudge — never mid-drag or while the chat is open. */
  @media (hover: hover) {
    #cw-launcher:hover ~ #cw-tip { opacity: 1; transform: translate(0, -50%); }
  }
  #cw-launcher:focus-visible ~ #cw-tip,
  #cw-launcher-wrap.cw-tip-nudge #cw-tip { opacity: 1; transform: translate(0, -50%); }
  #cw-launcher.cw-dragging ~ #cw-tip,
  #cw-launcher[aria-expanded='true'] ~ #cw-tip,
  #cw-launcher-wrap.cw-docked #cw-tip { opacity: 0 !important; }
  @keyframes cw-tip-pulse {
    0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
    70% { box-shadow: 0 0 0 7px rgba(34,197,94,0); }
    100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
  }
  /* ---- The chat invitation ------------------------------------------
     An animated card beside the launcher inviting the visitor to ask a
     question: on for sales demos, a per-bot switch for everyone else
     (Interactions tab). Desktop: beside the bubble, on the side facing the
     middle of the screen, bottoms level (tops level in the upper half).
     Phone: above it (below in the upper half). While it's up, ripples
     pulse out of the bubble and the hover hint stands down. */
  #cw-launcher-wrap { --cw-half: 64px; }
  #cw-launcher-wrap.cw-small { --cw-half: 32px; }
  @media (max-width: 640px) {
    #cw-launcher-wrap { --cw-half: 44px; }
    #cw-launcher-wrap.cw-small { --cw-half: 22px; }
  }
  #cw-launcher-wrap.cw-callout-on::before,
  #cw-launcher-wrap.cw-callout-on::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: 50%;
    border: 2px solid var(--cw-accent, #2563EB);
    pointer-events: none;
    opacity: 0;
    animation: cw-ripple 2.4s cubic-bezier(0.2, 0.6, 0.4, 1) infinite;
  }
  #cw-launcher-wrap.cw-callout-on::after { animation-delay: 1.2s; }
  #cw-launcher-wrap.cw-callout-on #cw-tip { display: none; }
  #cw-callout {
    position: absolute;
    right: calc(100% + 18px);
    bottom: 0;
    z-index: 3;
    display: none;
    width: 270px;
    color: #111827;
    text-align: left;
    font: 400 14px/1.45 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    transform-origin: 100% 100%;
  }
  #cw-launcher-wrap.cw-callout-on #cw-callout {
    display: block;
    animation: cw-callout-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both, cw-callout-float 3.6s ease-in-out 0.7s infinite;
  }
  #cw-launcher-wrap.cw-tip-right #cw-callout { right: auto; left: calc(100% + 18px); transform-origin: 0 100%; }
  #cw-launcher-wrap.cw-upper #cw-callout { bottom: auto; top: 0; }
  #cw-launcher.cw-dragging ~ #cw-callout,
  #cw-launcher[aria-expanded='true'] ~ #cw-callout,
  #cw-launcher-wrap.cw-docked #cw-callout { display: none !important; }
  .cw-callout-card {
    position: relative;
    padding: 14px 16px 16px;
    border: 2px solid transparent;
    border-radius: 18px;
    background:
      linear-gradient(#fff, #fff) padding-box,
      linear-gradient(120deg, var(--cw-accent, #2563EB), #ec4899, #f59e0b, #22d3ee, var(--cw-accent, #2563EB)) border-box;
    background-size: 100% 100%, 300% 300%;
    box-shadow: 0 18px 48px rgba(17,24,39,0.24), 0 2px 6px rgba(17,24,39,0.08);
    animation: cw-callout-border 5s ease-in-out infinite alternate;
  }
  .cw-callout-tail {
    position: absolute;
    right: -8px;
    bottom: calc(var(--cw-half) - 8px);
    z-index: 1;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    background: #fff;
    transform: rotate(45deg);
  }
  #cw-launcher-wrap.cw-tip-right .cw-callout-tail { right: auto; left: -8px; }
  #cw-launcher-wrap.cw-upper .cw-callout-tail { bottom: auto; top: calc(var(--cw-half) - 8px); }
  .cw-callout-close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cw-callout-close:hover { background: #e5e7eb; color: #111827; }
  .cw-callout-close:focus-visible,
  .cw-callout-cta:focus-visible { outline: 2px solid var(--cw-accent, #2563EB); outline-offset: 2px; }
  .cw-callout-head { display: flex; align-items: center; gap: 10px; padding-right: 22px; }
  .cw-callout-avatar {
    flex: none;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(135deg, var(--cw-accent, #2563EB), #ec4899);
    box-shadow: 0 0 0 0 rgba(236,72,153,0.45);
    animation: cw-glow 2.4s ease-out infinite;
  }
  .cw-callout-who { display: flex; flex-direction: column; min-width: 0; }
  .cw-callout-who strong { font-size: 15px; font-weight: 700; }
  .cw-wave { display: inline-block; transform-origin: 70% 70%; animation: cw-wave 2.4s ease-in-out infinite; }
  .cw-callout-status { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280; }
  .cw-callout-status i { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; }
  .cw-callout-typing { display: flex; gap: 5px; padding: 14px 2px 6px; }
  .cw-callout-typing i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #9ca3af;
    animation: cw-typing 1s ease-in-out infinite;
  }
  .cw-callout-typing i:nth-child(2) { animation-delay: 0.15s; }
  .cw-callout-typing i:nth-child(3) { animation-delay: 0.3s; }
  .cw-callout-msg { display: none; margin: 10px 0 12px; }
  .cw-callout-cta {
    position: relative;
    overflow: hidden;
    display: none;
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 14px;
    border: none;
    border-radius: 12px;
    background: var(--cw-accent, #2563EB);
    color: #fff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(17,24,39,0.18);
  }
  .cw-callout-cta::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%);
    transform: translateX(-100%);
    animation: cw-shimmer 3.2s ease-in-out 1s infinite;
  }
  .cw-callout-arrow { display: inline-block; animation: cw-nudge-x 1.4s ease-in-out infinite; }
  #cw-callout.cw-callout-typed .cw-callout-typing { display: none; }
  #cw-callout.cw-callout-typed .cw-callout-msg { display: block; animation: cw-rise 0.4s ease both; }
  #cw-callout.cw-callout-typed .cw-callout-cta { display: flex; animation: cw-rise 0.4s ease 0.12s both; }
  @media (max-width: 640px) {
    #cw-callout { right: 0; bottom: calc(100% + 14px); width: min(270px, calc(100vw - 24px)); transform-origin: 100% 100%; }
    #cw-launcher-wrap.cw-tip-right #cw-callout { left: 0; right: auto; transform-origin: 0 100%; }
    #cw-launcher-wrap.cw-upper #cw-callout { bottom: auto; top: calc(100% + 14px); }
    .cw-callout-tail { right: calc(var(--cw-half) - 8px); bottom: -8px; }
    #cw-launcher-wrap.cw-tip-right .cw-callout-tail { left: calc(var(--cw-half) - 8px); right: auto; }
    #cw-launcher-wrap.cw-upper .cw-callout-tail { top: -8px; bottom: auto; }
  }
  @keyframes cw-ripple { 0% { transform: scale(1); opacity: 0.55; } 100% { transform: scale(1.6); opacity: 0; } }
  @keyframes cw-callout-in { 0% { opacity: 0; transform: scale(0.6) translateY(12px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }
  @keyframes cw-callout-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
  @keyframes cw-callout-border { 0% { background-position: 0 0, 0% 50%; } 100% { background-position: 0 0, 100% 50%; } }
  @keyframes cw-glow { 0% { box-shadow: 0 0 0 0 rgba(236,72,153,0.45); } 70% { box-shadow: 0 0 0 10px rgba(236,72,153,0); } 100% { box-shadow: 0 0 0 0 rgba(236,72,153,0); } }
  @keyframes cw-wave { 0%, 60%, 100% { transform: rotate(0); } 10%, 30% { transform: rotate(16deg); } 20%, 40% { transform: rotate(-8deg); } }
  @keyframes cw-typing { 0%, 60%, 100% { transform: translateY(0); opacity: 0.5; } 30% { transform: translateY(-5px); opacity: 1; } }
  @keyframes cw-rise { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes cw-shimmer { 0% { transform: translateX(-100%); } 45%, 100% { transform: translateX(100%); } }
  @keyframes cw-nudge-x { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(4px); } }
  @media (prefers-reduced-motion: reduce) {
    #cw-launcher-wrap.cw-callout-on::before,
    #cw-launcher-wrap.cw-callout-on::after { display: none; }
    #cw-launcher-wrap.cw-callout-on #cw-callout { animation: cw-rise 0.3s ease both; }
    .cw-callout-card, .cw-callout-avatar, .cw-wave, .cw-callout-cta::after, .cw-callout-arrow { animation: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    #cw-launcher,
    #cw-size-toggle { transition: none; }
    #cw-tip { transition: opacity 0.18s ease; transform: translate(0, -50%) !important; }
    .cw-tip-dot { animation: none; }
    .cw-agent-head,
    .cw-agent-eye,
    .cw-agent-mouth { animation: none; }
  }
  #cw-frame {
    /* The starting spot: just above the launcher's own default corner.
       positionPanel() overrides these inline once the panel opens, so it
       stays beside the launcher wherever the visitor has dragged it — or
       where they dragged the panel itself by its header.
       Expanded (the header's expand button), the width and height are
       inline too — see applyExpandedSize(). .cw-expanded only marks it. */
    position: fixed;
    bottom: 160px;
    right: 20px;
    width: min(380px, calc(100vw - 32px));
    height: 480px;
    border: none;
    border-radius: 16px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.28);
    z-index: 2147483000;
    background: white;
    color-scheme: light;
  }
  @media (max-width: 640px) {
    #cw-frame {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      border-radius: 0;
    }
    /* Minimized on a phone: the header bar docks along the bottom edge
       (its height is inline) instead of filling the screen, and the
       launcher steps aside — the bar is the way back into the chat here,
       and there's no room for both in that corner. */
    #cw-frame.cw-minimized { top: auto; }
    #cw-launcher-wrap.cw-docked { display: none; }
  }
`;

function safeGuard(fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.error('[chat-widget] Unexpected error (isolated from the host page):', err);
  }
}

async function fetchSession(backendOrigin: string, botId: string): Promise<SessionInfo> {
  const none = (failure: SessionFailure): SessionInfo => ({ token: null, expiresAt: null, failure, theme: null, callout: false });

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), SESSION_REQUEST_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${backendOrigin}/api/session/${encodeURIComponent(botId)}`, { signal: controller.signal });
  } catch {
    return none({ kind: 'network', permanent: false });
  } finally {
    clearTimeout(timeout);
  }

  let body: { token?: string; expiresAt?: number; now?: number; error?: string; theme?: unknown; callout?: unknown } = {};
  try {
    body = await res.json();
  } catch {
    // No JSON body — the status alone is enough to classify below.
  }
  if (res.ok && body.token) {
    // Expiry on this device's clock: the server says when the token
    // expires and what time it is there, so the time left is what counts —
    // a device clock minutes ahead would otherwise see every token as
    // about to expire and renew it without end.
    const expiresAt =
      typeof body.expiresAt === 'number' && typeof body.now === 'number' ? Date.now() + (body.expiresAt - body.now) : (body.expiresAt ?? null);
    return { token: body.token, expiresAt, failure: null, theme: parseServerTheme(body.theme), callout: body.callout === true };
  }

  // 403 and 404 are the server saying "never, as configured" — see
  // server/src/http/routes/session.ts. Everything else could be transient.
  if (res.status === 403) return none({ kind: 'unauthorized', permanent: true, status: 403, detail: body.error });
  if (res.status === 404) return none({ kind: 'unknown_bot', permanent: true, status: 404, detail: body.error });
  return none({ kind: 'server', permanent: false, status: res.status, detail: body.error });
}

const warnedFailures = new Set<string>();

/** The one place the real reason surfaces — in the site owner's console,
 * once per cause, never to the visitor. A warning rather than an error
 * because the host page itself is fine. */
function warnSessionFailure(botId: string, failure: SessionFailure): void {
  const key = `${failure.kind}:${failure.status ?? ''}`;
  if (warnedFailures.has(key)) return;
  warnedFailures.add(key);

  const status = failure.status ? ` (HTTP ${failure.status}${failure.detail ? `: ${failure.detail}` : ''})` : '';
  const hint = {
    unauthorized:
      'This domain is not verified for this bot. In the dashboard, open the bot → Install and add this exact hostname — a parent domain does not cover subdomains or other ports.',
    unknown_bot: 'No bot has this id — check the data-bot-id attribute on the widget script tag.',
    server: 'The chat service returned an error. If it keeps happening, check the Worker logs.',
    network: 'The chat service could not be reached from this page.',
  }[failure.kind];
  console.warn(`[chat-widget] Could not start a chat session for bot "${botId}" on ${location.origin}${status}. ${hint}`);
}

function init() {
  // Read now, synchronously: document.currentScript is null by the time a
  // deferred callback runs.
  const currentScript = document.currentScript as HTMLScriptElement | null;
  // A snippet pasted into <head> without `defer`, or one a tag manager
  // injects before <body> is parsed: there is nothing to append the
  // launcher to yet, so the rest waits for the document.
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', () => safeGuard(() => initWith(currentScript)), { once: true });
    return;
  }
  initWith(currentScript);
}

function initWith(currentScript: HTMLScriptElement | null) {
  if (!currentScript) {
    console.error('[chat-widget] Could not locate the loader <script> tag.');
    return;
  }

  if ((window as unknown as { ChatWidget?: unknown }).ChatWidget) {
    return; // already initialized (e.g. the snippet was included twice)
  }

  const botId = currentScript.dataset.botId;
  // The snippet's own colour and logo are only the first guess: the
  // session response carries what the dashboard says now (see
  // applyServerTheme), and that wins. Every snippet the dashboard ever
  // handed out baked in the colour it had at copy time, so trusting the
  // attribute meant a change in the Customize tab never reached the site.
  let accent = currentScript.dataset.accent;
  let logoUrl = currentScript.dataset.logo;
  // The customer's own launcher video, overriding the one this bundle
  // ships; this is the attribute the dashboard will write once customers
  // can pick from a set of videos.
  const videoUrl = currentScript.dataset.video;
  const prefersReducedMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (!botId) {
    console.error('[chat-widget] Missing required data-bot-id attribute.');
    return;
  }

  const backendOrigin = new URL(currentScript.src, location.href).origin;
  // Workers static assets redirect "/v1/panel.html" -> "/v1/panel" by
  // default; use the canonical extensionless URL directly to skip that hop.
  const panelUrl = `${backendOrigin}/v1/panel`;

  const host = document.createElement('div');
  host.id = 'chat-widget-host';
  // The shadow root protects everything *inside* it from host page CSS,
  // but the host element itself is still a normal light-DOM node the host
  // page's own stylesheet can select (by tag, by "div", by "*") — and a
  // `transform`/`filter`/`perspective` on this element would create a new
  // containing block for our `position: fixed` descendants inside the
  // shadow root, silently relocating them regardless of encapsulation.
  // Inline `!important` beats any external selector-based rule (including
  // `!important` ones, since inline style specificity is higher than any
  // selector), so this is the actual defense — the shadow boundary alone
  // isn't enough. See PHASE-NOTES.md and /hostile, which exists to prove
  // this holds rather than assume it.
  host.setAttribute(
    'style',
    'all: initial !important; display: block !important; position: static !important; ' +
      'z-index: auto !important; pointer-events: auto !important;',
  );
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = LAUNCHER_CSS;
  shadow.appendChild(styleEl);

  // What's positioned and dragged; holds the launcher button and its size
  // toggle side by side.
  const launcher = document.createElement('div');
  launcher.id = 'cw-launcher-wrap';
  if (accent) launcher.style.setProperty('--cw-accent', accent);

  const bubble = document.createElement('button');
  bubble.type = 'button';
  bubble.id = 'cw-launcher';
  bubble.setAttribute('aria-label', 'Open chat');
  bubble.setAttribute('aria-expanded', 'false');

  // The <video> currently on the launcher, if the customer supplied one —
  // held so the panel opening can pause it (see openPanel/closePanel).
  let launcherVideo: HTMLVideoElement | null = null;

  /** Falls back to the built-in animated agent. Safe as a last resort
   * precisely because it's inline: no URL to fail, nothing to fetch. */
  function showBuiltInAgent() {
    launcherVideo = null;
    bubble.innerHTML = AGENT_VIDEO_SVG;
  }

  /** Puts the launcher's face on the bubble: the customer's video, else
   * their logo, else the built-in agent. Everything here is built with the
   * DOM API rather than innerHTML — the URLs come from this script's data
   * attributes or the dashboard (both the site owner's choice), but real
   * nodes also get a real `error` listener, so a 404 or a strict CSP
   * degrades to the animation instead of an empty circle. Replaces
   * whatever face was there before, so it can be re-run when the
   * dashboard's logo turns out to differ from the snippet's. */
  function renderLauncherFace() {
    launcherVideo?.pause();
    launcherVideo = null;
    bubble.textContent = '';
    // A customer's own video wins; then their logo (the pre-video
    // behaviour, kept so an existing embed doesn't change under them); then
    // the agent video this bundle ships with.
    // A stored launcher is root-relative ("/v1/..."), because the same
    // value has to work on localhost and in production. Resolve it against
    // the backend, never the host page — the customer's own site does not
    // serve our files.
    const stored = currentBuiltInLauncher(videoUrl || logoUrl);
    const mediaUrl = stored && stored.startsWith('/') ? `${backendOrigin}${stored}` : stored;
    if (mediaUrl && !VIDEO_URL_PATTERN.test(mediaUrl)) {
      // A .gif/.webp/.apng animates on its own in an <img>; a still logo
      // lands here too and simply doesn't move.
      const img = document.createElement('img');
      img.className = 'cw-launcher-media';
      img.alt = '';
      img.src = mediaUrl;
      // Only while it's still the face: a detached image keeps loading,
      // and the snippet's old logo failing after the dashboard's replaced
      // it would otherwise wipe the new one.
      img.addEventListener('error', () => {
        if (img.parentNode === bubble) showBuiltInAgent();
      });
      bubble.appendChild(img);
      return;
    }

    const sources = mediaUrl ? [mediaUrl] : __CW_LAUNCHER_MEDIA__.map((file) => `${backendOrigin}/${file}`);
    if (!sources.length) {
      showBuiltInAgent();
      return;
    }

    const video = document.createElement('video');
    video.className = 'cw-launcher-media';
    // Silent, always: the property (not just the attribute) is what Safari
    // and Chrome check before allowing autoplay, and a launcher that made
    // noise on someone's page would be unforgivable.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', ''); // older iOS reads the attribute, not the property
    video.disablePictureInPicture = true;
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true'); // the button's own label already says "Open chat"
    video.loop = !prefersReducedMotion;
    video.autoplay = !prefersReducedMotion;

    // A <video> with <source> children never fires `error` on the element
    // itself when every candidate fails — only each <source> does. So count
    // them: the drawn agent takes over once the last one is out.
    let sourcesFailed = 0;
    for (const url of sources) {
      const source = document.createElement('source');
      source.src = url;
      source.addEventListener('error', () => {
        if (++sourcesFailed === sources.length && video.parentNode === bubble) showBuiltInAgent();
      });
      video.appendChild(source);
    }
    // A single src= that fails still reports here.
    video.addEventListener('error', () => {
      if (video.parentNode === bubble) showBuiltInAgent();
    });

    bubble.appendChild(video);
    launcherVideo = video;
    playLauncherVideo();
  }

  /** Autoplay can still be refused (a data-saver mode, Edge's "limit
   * autoplay", a policy we don't control). The refusal leaves the first
   * frame painted, which is a passable launcher, and every browser lifts
   * the block after the visitor's first interaction with the page — so
   * that's when it's retried, quietly. */
  function playLauncherVideo() {
    if (!launcherVideo || prefersReducedMotion) return;
    safeGuard(() => {
      const played = launcherVideo?.play();
      if (played && typeof played.catch === 'function') played.catch(() => armPlayOnGesture());
    });
  }

  const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;
  let gestureArmed = false;
  function armPlayOnGesture() {
    if (gestureArmed) return;
    gestureArmed = true;
    const retry = () => {
      gestureArmed = false;
      for (const type of GESTURE_EVENTS) document.removeEventListener(type, retry, true);
      if (launcherInView()) playLauncherVideo();
    };
    for (const type of GESTURE_EVENTS) document.addEventListener(type, retry, { capture: true, passive: true });
  }

  renderLauncherFace();

  /** Brings the launcher in line with the dashboard's current colour and
   * logo. Runs on every session response — the first one arrives before
   * the launcher is revealed, so in the normal case the visitor never sees
   * the snippet's stale values at all. */
  function applyServerTheme(theme: ServerTheme) {
    if (theme.accent && theme.accent !== accent) {
      accent = theme.accent;
      launcher.style.setProperty('--cw-accent', accent);
    }
    const nextLogo = theme.logoUrl ?? undefined;
    if (nextLogo !== logoUrl) {
      logoUrl = nextLogo;
      // A customer's own video still outranks the logo — nothing to redraw.
      if (!videoUrl) {
        renderLauncherFace();
        if (launcherInView() && !disabled) playLauncherVideo();
      }
    }
  }

  const sizeToggle = document.createElement('button');
  sizeToggle.type = 'button';
  sizeToggle.id = 'cw-size-toggle';

  // The hint beside the launcher (see #cw-tip in LAUNCHER_CSS). After the
  // button, so the stylesheet can show it from the button's own :hover
  // and :focus-visible with a sibling selector.
  const tip = document.createElement('div');
  tip.id = 'cw-tip';
  tip.setAttribute('aria-hidden', 'true');
  tip.innerHTML =
    '<span class="cw-tip-title"><span class="cw-tip-dot"></span>Need help? Chat with us</span>' +
    '<span class="cw-tip-sub"><span class="cw-tip-mouse">Click to start · drag to move</span><span class="cw-tip-touch">Tap to start a chat</span></span>';

  launcher.style.display = 'none'; // revealed once the first session check passes — see the bottom of init()
  launcher.appendChild(bubble);
  launcher.appendChild(sizeToggle);
  launcher.appendChild(tip);
  // The chat invitation (#cw-callout in LAUNCHER_CSS): built for every
  // bot, shown only when the session says this bot has it switched on.
  const callout = document.createElement('div');
  callout.id = 'cw-callout';
  callout.setAttribute('role', 'complementary');
  callout.setAttribute('aria-label', 'Chat invitation');
  callout.innerHTML = CALLOUT_HTML;
  launcher.appendChild(callout);
  shadow.appendChild(launcher);

  let iframe: HTMLIFrameElement | null = null;
  let isOpen = false;
  // Open, but folded down to the panel's header bar by its minimize button.
  // isOpen stays true throughout: the frame is still on screen.
  let isMinimized = false;
  // At the expand button's larger size. Kept for the rest of this page view
  // — through a minimize, a close and reopen, or a trip through a
  // phone-sized window — but never stored, so the next page starts normal.
  let isExpanded = false;
  // What the panel was last told about its expand button, so a window
  // resize only messages it when something it shows actually changed.
  let layoutSent: { expandable: boolean; expanded: boolean } | null = null;
  // The expanded height just shrunk from. The panel reports its frame's
  // height a frame or two late, so one report of the expanded size can
  // still be on its way when Shrink lands — taken as the normal height, it
  // left the shrunk panel 800px tall (found by the e2e suite). Reports of
  // exactly this height are ignored until a different one arrives.
  let shrunkFromHeight: number | null = null;
  let iframeLoadFailed = false;
  // True when the first session check says this bot can never work here
  // (unverified domain, unknown id). The launcher is never shown — a
  // missing chat beats one that fails on the first message — and the
  // reason is in the console for the site owner.
  let disabled = false;
  // Set once the init handshake has been posted; before that a session
  // push would be ignored by the panel anyway (see panel/lib/messaging.ts).
  let panelReady = false;

  let session: SessionInfo | null = null;
  let sessionInFlight: Promise<SessionInfo> | null = null;
  let renewTimer: ReturnType<typeof setTimeout> | null = null;

  /** Whether the launcher is the chat's face on screen: the chat is closed,
   * or minimized beside it on a desktop (on a phone the docked bar takes
   * its place). Its video only plays then. */
  function launcherInView(): boolean {
    return !isOpen || (isMinimized && window.innerWidth > MOBILE_MAX_WIDTH_PX);
  }

  function postToPanel(message: LoaderToPanelMessage) {
    if (!iframe?.contentWindow) return;
    safeGuard(() => iframe!.contentWindow!.postMessage(message, backendOrigin));
  }

  function isFresh(s: SessionInfo | null): s is SessionInfo & { token: string; expiresAt: number } {
    return !!s?.token && !!s.expiresAt && s.expiresAt - Date.now() > RENEW_BEFORE_MS;
  }

  /** Fetches a session, remembers it, pushes it to the panel, and lines up
   * the next renewal. One request at a time — concurrent callers (the
   * renewal timer, a visibility change, the panel asking) share it. */
  function refreshSession(): Promise<SessionInfo> {
    if (sessionInFlight) return sessionInFlight;
    sessionInFlight = fetchSession(backendOrigin, botId as string).then((next) => {
      sessionInFlight = null;
      session = next;
      if (next.failure) warnSessionFailure(botId as string, next.failure);
      const theme = next.theme;
      if (theme) safeGuard(() => applyServerTheme(theme));
      if (next.callout) safeGuard(enableCallout);
      if (panelReady) {
        postToPanel({
          type: 'chat-widget:session',
          sessionToken: next.token,
          sessionExpiresAt: next.expiresAt,
          sessionFailure: next.failure,
        });
        scheduleRenewal(next);
      }
      return next;
    });
    return sessionInFlight;
  }

  /** Renews shortly before expiry — only while the panel exists, so a page
   * whose visitor never opens the chat isn't asking every 13 minutes. */
  function scheduleRenewal(s: SessionInfo) {
    if (renewTimer) clearTimeout(renewTimer);
    renewTimer = null;
    if (!s.token || !s.expiresAt) return;
    // Never sooner than half a minute: expiry comes from the server's clock
    // (fetchSession corrects for the difference when it can), and a
    // renewal every five seconds for the rest of the visit used up the
    // site's per-network session allowance for everyone behind that NAT.
    renewTimer = setTimeout(() => void refreshSession(), Math.max(s.expiresAt - Date.now() - RENEW_BEFORE_MS, 30_000));
  }

  // Background tabs throttle timers, so a laptop back from sleep can hold
  // an expired token — renew on the way back in, not on the first send.
  document.addEventListener('visibilitychange', () => {
    safeGuard(() => {
      if (document.visibilityState !== 'visible' || disabled) return;
      // Browsers may pause a looping video in a background tab and not
      // resume it — the launcher would come back frozen.
      if (launcherInView() && launcherVideo?.paused) playLauncherVideo();
      if (!panelReady) return;
      if (!isFresh(session)) void refreshSession();
    });
  });

  function ensureIframe(): HTMLIFrameElement | null {
    if (iframe) return iframe;
    const frame = document.createElement('iframe');
    frame.id = 'cw-frame';
    frame.src = panelUrl;
    frame.title = 'Chat';
    frame.style.display = 'none';

    frame.addEventListener('load', () => {
      safeGuard(async () => {
        const s = isFresh(session) ? session : await refreshSession();
        const layout = { expandable: canExpand(), expanded: isExpanded };
        const initMessage: LoaderToPanelMessage = {
          type: 'chat-widget:init',
          version: 1,
          botId: botId as string,
          accent,
          logoUrl,
          hostOrigin: location.origin,
          // Origin + path only — never location.search or location.hash.
          // See the comment on this field in shared/protocol.ts.
          hostPage: location.origin + location.pathname,
          sessionToken: s.token,
          sessionExpiresAt: s.expiresAt,
          sessionFailure: s.failure,
          ...layout,
        };
        postToPanel(initMessage);
        layoutSent = layout;
        panelReady = true;
        scheduleRenewal(s);
      });
    });

    // Degrade gracefully if the iframe document itself fails to load (e.g.
    // the backend is unreachable) — the launcher stays visible and usable
    // rather than opening onto a blank, permanently-loading rectangle.
    frame.addEventListener('error', () => {
      iframeLoadFailed = true;
      console.error('[chat-widget] The chat panel failed to load.');
    });

    shadow.appendChild(frame);
    iframe = frame;
    return frame;
  }

  // Strict origin check: only accept messages that (a) come from the exact
  // backend origin we pointed the iframe at, and (b) whose source window is
  // that exact iframe's contentWindow.
  window.addEventListener('message', (event: MessageEvent) => {
    safeGuard(() => {
      if (event.origin !== backendOrigin) return;
      if (!iframe || event.source !== iframe.contentWindow) return;

      const data = event.data as PanelToLoaderMessage | undefined;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'chat-widget:resize') {
        // Only the panel on show in full has a height worth following.
        // Closed, its hidden frame reports 0 — which, clamped up to the
        // 320px floor, used to shrink the panel for every later open.
        // Minimized, the frame is the header bar's height (minimizePanel),
        // and that bar is all the panel has to report.
        if (!isOpen || isMinimized || !(data.height > 0)) return;
        // Leaves the launcher room below the panel in its default corner.
        // Not on a phone: the stylesheet makes the panel full-screen there,
        // and an inline height beat it, leaving the panel 168px short with
        // the host page and launcher showing (and tappable) underneath.
        // Not while expanded either: that size is fixed (applyExpandedSize),
        // and this report is only the frame measuring it back.
        if (window.innerWidth <= MOBILE_MAX_WIDTH_PX) {
          if (iframe) iframe.style.height = '';
        } else if (!isExpanded) {
          if (shrunkFromHeight !== null && Math.round(data.height) === shrunkFromHeight) return; // late, from before Shrink
          shrunkFromHeight = null;
          const maxHeight = Math.max(PANEL_MIN_HEIGHT_PX, window.innerHeight - 168);
          const height = Math.min(Math.max(data.height, PANEL_MIN_HEIGHT_PX), maxHeight);
          if (iframe) iframe.style.height = `${height}px`;
        }
        if (isOpen) positionPanel();
      } else if (data.type === 'chat-widget:close') {
        closePanel();
      } else if (data.type === 'chat-widget:minimize') {
        minimizePanel(data.height);
      } else if (data.type === 'chat-widget:restore') {
        openPanel();
      } else if (data.type === 'chat-widget:expand') {
        setExpanded(data.expanded);
      } else if (data.type === 'chat-widget:move') {
        movePanel(data.phase, data.dx, data.dy);
      } else if (data.type === 'chat-widget:request-session') {
        void refreshSession(); // posts the result to the panel itself
      } else if (data.type === 'chat-widget:navigate') {
        navigateTo(data.url);
      }
      // 'chat-widget:ready' is informational only.
    });
  });

  // Set just before a link button navigates this tab, so the chat is open
  // again on the page that loads — read (and cleared) on the next init.
  const reopenKey = `chat-widget:reopen:${botId}`;

  /** A link button on the host's own site: go there in this tab. Anything
   * else is refused — the panel only asks for same-origin pages, and the
   * check here means a misbehaving frame can't send the visitor elsewhere. */
  function navigateTo(raw: string) {
    let target: URL;
    try {
      target = new URL(raw, location.href);
    } catch {
      return;
    }
    if (target.origin !== location.origin) return;
    // A link that only changes the #fragment doesn't load a new page, so
    // the panel simply stays open — and a flag left behind would pop it
    // open on some later visit instead.
    const samePage = target.href.split('#')[0] === location.href.split('#')[0] && target.hash !== '';
    // On a phone the panel is full-screen, so reopening it would cover the
    // very page the visitor asked to see — there it closes and stays closed.
    if (window.innerWidth <= MOBILE_MAX_WIDTH_PX) {
      if (isOpen && samePage) closePanel();
    } else if (isOpen && !samePage) {
      try {
        sessionStorage.setItem(reopenKey, '1');
      } catch {
        // Storage blocked — the page still changes, the chat just starts closed.
      }
    }
    location.assign(target.href);
  }

  function openPanel() {
    safeGuard(() => {
      if (disabled) return;
      endNudge();
      if (calloutWanted) closeCallout(); // they found the chat: no need to invite them again on this page
      const frame = ensureIframe();
      if (!frame || iframeLoadFailed) return;
      clearMinimized(); // opening a minimized chat brings the whole panel back
      frame.style.display = 'block';
      applyExpandedSize(); // an expanded chat comes back expanded, fitted to the window as it is now
      positionPanel(); // after display, so the frame has a size to place
      bubble.setAttribute('aria-expanded', 'true');
      launcherVideo?.pause(); // the agent is in the panel now — stop decoding frames behind it
      isOpen = true;
      frame.focus();
      postToPanel({ type: 'chat-widget:visibility', open: true, minimized: false });
    });
  }

  function closePanel() {
    safeGuard(() => {
      // Already closed: nothing to do — and above all, don't pull focus to
      // the launcher out of whatever the visitor is typing in (a host page
      // calling ChatWidget.close() on every route change, say).
      if (!isOpen) return;
      clearMinimized(); // so the next open is the whole panel, not the bar
      if (iframe) iframe.style.display = 'none';
      bubble.setAttribute('aria-expanded', 'false');
      playLauncherVideo();
      isOpen = false;
      bubble.focus(); // return focus to the launcher, not lose it into the page
      postToPanel({ type: 'chat-widget:visibility', open: false });
    });
  }

  /** The panel's minimize button: folds the frame down to its header bar,
   * which stays beside the launcher (positionPanel places it like the
   * panel) with the conversation kept behind it. Closing hides it all;
   * this keeps the chat in view, a click away. Meanwhile the launcher is a
   * way back in again, so it reads as collapsed and its video resumes. */
  function minimizePanel(reportedHeight: unknown) {
    if (!isOpen || isMinimized || !iframe) return;
    const height =
      typeof reportedHeight === 'number' && Number.isFinite(reportedHeight)
        ? Math.min(Math.max(Math.ceil(reportedHeight), MINIMIZED_MIN_HEIGHT_PX), MINIMIZED_MAX_HEIGHT_PX)
        : MINIMIZED_FALLBACK_HEIGHT_PX;
    isMinimized = true;
    iframe.classList.add('cw-minimized');
    iframe.style.height = `${height}px`;
    // The bar is the normal width even from an expanded panel — minimizing
    // is for getting it out of the way. Restoring brings the size back.
    iframe.style.width = '';
    launcher.classList.add('cw-docked');
    positionPanel();
    bubble.setAttribute('aria-expanded', 'false');
    if (launcherInView()) playLauncherVideo();
    postToPanel({ type: 'chat-widget:visibility', open: true, minimized: true });
  }

  /** Undoes minimizePanel: the frame goes back to the stylesheet's size
   * until the panel next reports its content height. */
  function clearMinimized() {
    if (!isMinimized) return;
    isMinimized = false;
    launcher.classList.remove('cw-docked');
    if (iframe) {
      iframe.classList.remove('cw-minimized');
      iframe.style.height = '';
    }
  }

  /** Whether the header's expand button applies: on a desktop. A phone's
   * panel is already the whole screen (see LAUNCHER_CSS). */
  function canExpand(): boolean {
    return window.innerWidth > MOBILE_MAX_WIDTH_PX;
  }

  /** Writes the expanded size onto the frame: EXPANDED_WIDTH_PX by
   * EXPANDED_HEIGHT_PX, fitted inside the window's edge margins and never
   * shorter than the normal panel's floor. Only for an expanded panel open
   * in full on a desktop — a minimized bar keeps its own height, and on a
   * phone the stylesheet makes the panel full-screen. */
  function applyExpandedSize() {
    if (!iframe || !isExpanded || isMinimized || !canExpand()) return;
    const width = Math.min(EXPANDED_WIDTH_PX, window.innerWidth - 2 * EDGE_MARGIN_PX);
    const height = Math.max(PANEL_MIN_HEIGHT_PX, Math.min(EXPANDED_HEIGHT_PX, window.innerHeight - 2 * EDGE_MARGIN_PX));
    iframe.style.width = `${width}px`;
    iframe.style.height = `${height}px`;
  }

  /** The panel's expand button: the larger size, or back to the normal one
   * — the stylesheet's width and, as after a minimize, its height until the
   * panel next reports (see 'chat-widget:resize'). Either way the panel is
   * placed beside the launcher again, and told, so its button follows. */
  function setExpanded(next: unknown) {
    if (typeof next !== 'boolean' || !isOpen || isMinimized || !iframe) return;
    // A phone's panel is already the whole screen, so there it stays as it
    // is — and the answer below takes the button away again.
    const allowed = !next || canExpand();
    if (allowed && next !== isExpanded) {
      isExpanded = next;
      iframe.classList.toggle('cw-expanded', next);
      if (next) applyExpandedSize();
      else {
        shrunkFromHeight = Math.round(parseFloat(iframe.style.height)) || null;
        iframe.style.width = iframe.style.height = '';
      }
      positionPanel();
    }
    postLayout(true);
  }

  /** Tells the panel what its expand button should show: only when that
   * changed, unless the panel just asked — then always, so a panel that is
   * somehow out of step is put right rather than left waiting. */
  function postLayout(force = false) {
    if (!panelReady) return;
    const layout = { expandable: canExpand(), expanded: isExpanded };
    if (!force && layoutSent?.expandable === layout.expandable && layoutSent?.expanded === layout.expanded) return;
    layoutSent = layout;
    postToPanel({ type: 'chat-widget:layout', ...layout });
  }

  // --- The panel is movable too -----------------------------------------
  // Dragging its header (the panel reports it: 'chat-widget:move') puts it
  // anywhere on the screen, remembered per bot like the launcher's spot.
  // null means "beside the launcher", which is also where moving the
  // launcher itself sends it back to.
  const panelPositionKey = `chat-widget:panel-pos:${botId}`;
  let panelPos: { left: number; top: number } | null = null;
  // Where the frame was when the current header drag began.
  let panelDragOrigin: { left: number; top: number } | null = null;

  function loadPanelPos() {
    try {
      const raw = localStorage.getItem(panelPositionKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { left?: unknown; top?: unknown };
      if (typeof parsed.left === 'number' && typeof parsed.top === 'number') panelPos = { left: parsed.left, top: parsed.top };
    } catch {
      // Storage blocked or garbage in it — beside the launcher is fine.
    }
  }

  function savePanelPos() {
    try {
      if (panelPos) localStorage.setItem(panelPositionKey, JSON.stringify(panelPos));
      else localStorage.removeItem(panelPositionKey);
    } catch {
      // Private mode / quota — it just won't be remembered.
    }
  }

  /** The launcher moved: the panel goes back to following it. */
  function releasePanelPos() {
    if (!panelPos) return;
    panelPos = null;
    savePanelPos();
  }

  /** A header drag reported by the panel. Desktop only — on a phone the
   * panel is the whole screen — and only while it's open. */
  function movePanel(phase: unknown, dx: unknown, dy: unknown) {
    if (!iframe || !isOpen || !canExpand()) return;
    if (phase === 'start') {
      panelDragOrigin = { left: iframe.offsetLeft, top: iframe.offsetTop };
    } else if (phase === 'move') {
      if (!panelDragOrigin || typeof dx !== 'number' || typeof dy !== 'number' || !Number.isFinite(dx) || !Number.isFinite(dy)) return;
      panelPos = { left: panelDragOrigin.left + dx, top: panelDragOrigin.top + dy };
      positionPanel();
    } else if (phase === 'end') {
      if (!panelDragOrigin) return;
      panelDragOrigin = null;
      if (panelPos) {
        // Stored where it ended up on screen, not where the pointer was let go.
        panelPos = { left: iframe.offsetLeft, top: iframe.offsetTop };
        savePanelPos();
      }
    }
  }

  /** Puts the open panel next to the launcher, fully inside the viewport
   * and, whenever the screen allows, not covering the launcher: above it
   * first, then below, then beside it — or, once the visitor has dragged
   * it by its header, where they left it. Uses offset* (layout values)
   * rather than getBoundingClientRect so the :hover scale doesn't skew
   * the maths. */
  function positionPanel() {
    if (!iframe) return;
    if (window.innerWidth <= MOBILE_MAX_WIDTH_PX) {
      // The stylesheet takes it full-screen; inline coordinates (or a
      // height, or an expanded width, left from a wider window) would
      // fight it. A minimized bar keeps its height — the stylesheet docks
      // it along the bottom.
      iframe.style.left = iframe.style.top = iframe.style.right = iframe.style.bottom = '';
      iframe.style.width = '';
      if (!isMinimized) iframe.style.height = '';
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const b = { left: launcher.offsetLeft, top: launcher.offsetTop, width: launcher.offsetWidth, height: launcher.offsetHeight };
    const width = iframe.offsetWidth || Math.min(380, vw - 32);
    const height = iframe.offsetHeight || 480;

    const maxLeft = Math.max(EDGE_MARGIN_PX, vw - width - EDGE_MARGIN_PX);
    const maxTop = Math.max(EDGE_MARGIN_PX, vh - height - EDGE_MARGIN_PX);
    const clampX = (x: number) => Math.min(Math.max(x, EDGE_MARGIN_PX), maxLeft);
    const clampY = (y: number) => Math.min(Math.max(y, EDGE_MARGIN_PX), maxTop);

    if (panelPos) {
      iframe.style.left = `${clampX(panelPos.left)}px`;
      iframe.style.top = `${clampY(panelPos.top)}px`;
      iframe.style.right = 'auto';
      iframe.style.bottom = 'auto';
      return;
    }

    // Stacked above/below: edges aligned with the launcher on whichever side
    // of the screen it sits. Beside it: bottoms level.
    const onRightHalf = b.left + b.width / 2 > vw / 2;
    const alignedLeft = clampX(onRightHalf ? b.left + b.width - width : b.left);
    const alignedTop = clampY(b.top + b.height - height);
    const above = b.top - PANEL_GAP_PX - height;
    const below = b.top + b.height + PANEL_GAP_PX;
    const leftOf = b.left - PANEL_GAP_PX - width;
    const rightOf = b.left + b.width + PANEL_GAP_PX;
    const sides = onRightHalf ? [leftOf, rightOf] : [rightOf, leftOf];

    let left: number;
    let top: number;
    if (above >= EDGE_MARGIN_PX) {
      left = alignedLeft;
      top = above;
    } else if (below <= maxTop) {
      left = alignedLeft;
      top = below;
    } else if (sides[0] >= EDGE_MARGIN_PX && sides[0] <= maxLeft) {
      left = sides[0];
      top = alignedTop;
    } else if (sides[1] >= EDGE_MARGIN_PX && sides[1] <= maxLeft) {
      left = sides[1];
      top = alignedTop;
    } else {
      // A launcher parked mid-screen in a viewport too small for both — the
      // overlap is unavoidable, so keep the panel whole and on screen.
      left = alignedLeft;
      top = clampY(above);
    }

    iframe.style.left = `${left}px`;
    iframe.style.top = `${top}px`;
    iframe.style.right = 'auto';
    iframe.style.bottom = 'auto';
  }

  // --- The launcher is movable ------------------------------------------
  // A pointer drag (mouse, touch or pen) or the arrow keys while focused
  // move it; the spot is remembered per bot in localStorage, so it's where
  // the visitor left it on their next page. null means "the default
  // corner", which is the stylesheet's bottom/right and needs no inline
  // position at all.
  const positionKey = `chat-widget:launcher-pos:${botId}`;
  let launcherPos: { left: number; top: number } | null = null;

  function loadLauncherPos() {
    try {
      const raw = localStorage.getItem(positionKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { left?: unknown; top?: unknown };
      if (typeof parsed.left === 'number' && typeof parsed.top === 'number') launcherPos = { left: parsed.left, top: parsed.top };
    } catch {
      // Storage blocked or garbage in it — the default corner is fine.
    }
  }

  function saveLauncherPos() {
    try {
      if (launcherPos) localStorage.setItem(positionKey, JSON.stringify(launcherPos));
    } catch {
      // Private mode / quota — it just won't be remembered.
    }
  }

  /** Writes launcherPos to the element, clamped so it stays fully on
   * screen (a viewport that shrank since it was saved would otherwise
   * strand it). Only meaningful once the launcher is displayed — offset*
   * of a display:none element are all zero. */
  function applyLauncherPos() {
    if (!launcherPos) {
      launcher.style.left = launcher.style.top = launcher.style.right = launcher.style.bottom = '';
      placeTip();
      return;
    }
    const maxLeft = Math.max(EDGE_MARGIN_PX, window.innerWidth - launcher.offsetWidth - EDGE_MARGIN_PX);
    const maxTop = Math.max(EDGE_MARGIN_PX, window.innerHeight - launcher.offsetHeight - EDGE_MARGIN_PX);
    launcherPos = {
      left: Math.min(Math.max(launcherPos.left, EDGE_MARGIN_PX), maxLeft),
      top: Math.min(Math.max(launcherPos.top, EDGE_MARGIN_PX), maxTop),
    };
    launcher.style.left = `${launcherPos.left}px`;
    launcher.style.top = `${launcherPos.top}px`;
    launcher.style.right = 'auto';
    launcher.style.bottom = 'auto';
    placeTip();
  }

  /** The hint goes on the side facing the middle of the screen, so it's
   * never pushed off the edge the launcher sits against. */
  function placeTip() {
    const centre = launcher.offsetLeft + launcher.offsetWidth / 2;
    launcher.classList.toggle('cw-tip-right', centre < window.innerWidth / 2);
    // The invitation grows away from the nearer edge: up from a launcher in
    // the lower half, down from one dragged into the upper half.
    launcher.classList.toggle('cw-upper', launcher.offsetTop + launcher.offsetHeight / 2 < window.innerHeight / 2);
  }

  // --- The launcher has two sizes -----------------------------------------
  // Large (the default) or small — half the size — toggled by the visitor
  // with the corner button and remembered per bot, like the position.
  const sizeKey = `chat-widget:launcher-size:${botId}`;
  let launcherSmall = false;

  function loadLauncherSize() {
    try {
      launcherSmall = localStorage.getItem(sizeKey) === 'small';
    } catch {
      // Storage blocked — large it is.
    }
  }

  function saveLauncherSize() {
    try {
      if (launcherSmall) localStorage.setItem(sizeKey, 'small');
      else localStorage.removeItem(sizeKey);
    } catch {
      // Private mode / quota — it just won't be remembered.
    }
  }

  function renderLauncherSize() {
    launcher.classList.toggle('cw-small', launcherSmall);
    sizeToggle.innerHTML = launcherSmall ? EXPAND_ICON_SVG : SHRINK_ICON_SVG;
    const label = launcherSmall ? 'Make chat button larger' : 'Make chat button smaller';
    sizeToggle.setAttribute('aria-label', label);
    sizeToggle.title = label;
  }

  function toggleLauncherSize() {
    const oldWidth = launcher.offsetWidth;
    const oldHeight = launcher.offsetHeight;
    launcherSmall = !launcherSmall;
    renderLauncherSize();
    saveLauncherSize();
    if (launcherPos) {
      // A dragged launcher shrinks/grows around its centre rather than its
      // top-left corner; the default one stays anchored to its corner.
      moveLauncherTo(
        launcherPos.left + (oldWidth - launcher.offsetWidth) / 2,
        launcherPos.top + (oldHeight - launcher.offsetHeight) / 2,
      );
      saveLauncherPos();
    } else if (isOpen) {
      positionPanel();
    }
  }

  sizeToggle.addEventListener('click', () => safeGuard(toggleLauncherSize));

  function moveLauncherTo(left: number, top: number) {
    launcherPos = { left, top };
    applyLauncherPos();
    if (isOpen) positionPanel();
  }

  let drag: { pointerId: number; startX: number; startY: number; originLeft: number; originTop: number; moved: boolean } | null =
    null;
  // A drag ends with the same pointerup that would fire `click` — this is
  // how the click handler knows to let that one go by.
  let suppressNextClick = false;

  bubble.addEventListener('pointerdown', (event: PointerEvent) => {
    safeGuard(() => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      endNudge();
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originLeft: launcher.offsetLeft,
        originTop: launcher.offsetTop,
        moved: false,
      };
      // Keeps the moves coming even when the pointer outruns the launcher.
      try {
        bubble.setPointerCapture(event.pointerId);
      } catch {
        // Not a capturable pointer (or the browser predates the API) —
        // dragging still works while the pointer stays over the launcher.
      }
    });
  });

  bubble.addEventListener('pointermove', (event: PointerEvent) => {
    safeGuard(() => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        drag.moved = true;
        bubble.classList.add('cw-dragging');
        releasePanelPos();
      }
      moveLauncherTo(drag.originLeft + dx, drag.originTop + dy);
    });
  });

  function endDrag(event: PointerEvent) {
    safeGuard(() => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const moved = drag.moved;
      drag = null;
      bubble.classList.remove('cw-dragging');
      if (bubble.releasePointerCapture) {
        try {
          bubble.releasePointerCapture(event.pointerId);
        } catch {
          // Already released (pointercancel) — nothing to do.
        }
      }
      if (!moved) return;
      saveLauncherPos();
      suppressNextClick = true;
      // The click, if any, is dispatched in this same task; a cancelled
      // drag has none, and the flag mustn't eat the visitor's next real tap.
      setTimeout(() => (suppressNextClick = false), 0);
    });
  }
  bubble.addEventListener('pointerup', endDrag);
  bubble.addEventListener('pointercancel', endDrag);

  bubble.addEventListener('click', () => {
    safeGuard(() => {
      if (suppressNextClick) {
        suppressNextClick = false;
        return;
      }
      // A minimized chat is brought back, not closed.
      if (isOpen && !isMinimized) closePanel();
      else openPanel();
    });
  });

  // Keyboard users move it too: arrows nudge, shift+arrows stride.
  bubble.addEventListener('keydown', (event: KeyboardEvent) => {
    safeGuard(() => {
      const step = event.shiftKey ? 40 : 10;
      const deltas: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      const delta = deltas[event.key];
      if (!delta) return;
      event.preventDefault();
      releasePanelPos();
      moveLauncherTo(launcher.offsetLeft + delta[0], launcher.offsetTop + delta[1]);
      saveLauncherPos();
    });
  });

  window.addEventListener('resize', () => {
    safeGuard(() => {
      if (revealed && launcherPos) applyLauncherPos();
      if (isOpen) {
        applyExpandedSize(); // refitted to the new window, or back from phone width
        // A window made shorter: the panel's height is clamped only when
        // the panel reports it (the resize message above), and a shorter
        // window prompts no report — so clamp it here, or the composer
        // ends up below the bottom edge until the chat is reopened.
        if (iframe && !isMinimized && !isExpanded && window.innerWidth > MOBILE_MAX_WIDTH_PX && iframe.style.height) {
          const maxHeight = Math.max(PANEL_MIN_HEIGHT_PX, window.innerHeight - 168);
          if (parseFloat(iframe.style.height) > maxHeight) iframe.style.height = `${maxHeight}px`;
        }
        positionPanel();
      }
      postLayout(); // crossing the phone breakpoint shows or hides the expand button
    });
  });

  loadLauncherPos();
  loadPanelPos();
  loadLauncherSize();
  renderLauncherSize();

  // Escape closes the panel from anywhere on the host page. A minimized bar
  // isn't in the visitor's way, so there the key is left to the page.
  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen && !isMinimized) safeGuard(closePanel);
  });

  // The first session check decides whether there's a chat to offer at all.
  // A permanent failure means no launcher (and a console warning); anything
  // else shows it. Capped so a slow network can't hide the chat — if a
  // permanent failure arrives after the cap, the launcher goes away again.
  let reopenAfterNavigate = false;
  try {
    reopenAfterNavigate = sessionStorage.getItem(reopenKey) === '1';
    sessionStorage.removeItem(reopenKey);
  } catch {
    // Storage blocked — start closed, as on any first visit.
  }

  // --- A one-time nudge ---------------------------------------------------
  // A visitor who hasn't touched the chat sees the hint once, a few seconds
  // after the launcher appears, for a few seconds — then never again on
  // this site for this bot. Any interaction first (a click, a drag, the
  // page opening the chat) counts as having found it, and cancels it.
  const nudgeKey = `chat-widget:nudged:${botId}`;
  let nudgeTimer: ReturnType<typeof setTimeout> | null = null;
  let nudgeDone = false;

  function scheduleNudge() {
    try {
      nudgeDone = nudgeDone || localStorage.getItem(nudgeKey) === '1';
    } catch {
      nudgeDone = true; // storage blocked: it couldn't be remembered, so it would come back on every page
    }
    if (nudgeDone || nudgeTimer) return;
    nudgeTimer = setTimeout(() => {
      safeGuard(() => {
        nudgeTimer = null;
        if (nudgeDone || isOpen || disabled || calloutWanted) return; // the invitation says it louder
        rememberNudge();
        launcher.classList.add('cw-tip-nudge');
        nudgeTimer = setTimeout(() => safeGuard(() => launcher.classList.remove('cw-tip-nudge')), NUDGE_SHOW_MS);
      });
    }, NUDGE_DELAY_MS);
  }

  function rememberNudge() {
    nudgeDone = true;
    try {
      localStorage.setItem(nudgeKey, '1');
    } catch {
      // Private mode — it simply won't be remembered.
    }
  }

  /** The visitor found the chat themselves: no nudge, now or later. */
  function endNudge() {
    if (nudgeTimer) clearTimeout(nudgeTimer);
    nudgeTimer = null;
    launcher.classList.remove('cw-tip-nudge');
    if (!nudgeDone) rememberNudge();
  }

  // --- The chat invitation --------------------------------------------
  // Arrives a moment after the launcher, "types" for a beat, then shows
  // its message and a button that opens the chat. It stays until the
  // visitor opens the chat or closes the invitation, and either one puts
  // it away for this page only: every page load (a reload included) greets
  // the visitor again, animation and all. Nothing is remembered.
  let calloutWanted = false;
  let calloutDismissed = false;
  let calloutTimer: ReturnType<typeof setTimeout> | null = null;
  let calloutTypingTimer: ReturnType<typeof setTimeout> | null = null;

  const calloutClosed = () => calloutDismissed;

  /** The session says this bot has the invitation switched on. */
  function enableCallout() {
    if (calloutWanted) return;
    calloutWanted = true;
    if (revealed) scheduleCallout();
  }

  function scheduleCallout() {
    if (!calloutWanted || calloutTimer || isOpen || calloutClosed() || launcher.classList.contains('cw-callout-on')) return;
    // One invitation at a time: the quieter nudge stands down for good.
    if (nudgeTimer) clearTimeout(nudgeTimer);
    nudgeTimer = null;
    launcher.classList.remove('cw-tip-nudge');
    calloutTimer = setTimeout(() => safeGuard(showCallout), CALLOUT_DELAY_MS);
  }

  function showCallout() {
    calloutTimer = null;
    if (isOpen || disabled || calloutClosed()) return;
    callout.classList.remove('cw-callout-typed');
    placeTip();
    launcher.classList.add('cw-callout-on');
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    calloutTypingTimer = setTimeout(() => safeGuard(() => callout.classList.add('cw-callout-typed')), reduceMotion ? 0 : CALLOUT_TYPING_MS);
  }

  /** Puts the invitation away until the next page load. */
  function closeCallout() {
    if (calloutTimer) clearTimeout(calloutTimer);
    if (calloutTypingTimer) clearTimeout(calloutTypingTimer);
    calloutTimer = calloutTypingTimer = null;
    calloutDismissed = true;
    launcher.classList.remove('cw-callout-on');
  }

  /** ChatWidget.invite(): the invitation again from the start, animation
   * and all, even after it was closed or on a bot that has it switched off.
   * For a page that wants to point the visitor at the chat — the demo page
   * calls it when a link on the copy of the prospect's site is clicked. */
  function replayCallout() {
    if (disabled || isOpen) return;
    calloutWanted = true;
    calloutDismissed = false;
    if (!revealed) return; // it arrives with the launcher
    if (calloutTimer) clearTimeout(calloutTimer);
    if (calloutTypingTimer) clearTimeout(calloutTypingTimer);
    if (nudgeTimer) clearTimeout(nudgeTimer);
    calloutTimer = calloutTypingTimer = nudgeTimer = null;
    launcher.classList.remove('cw-callout-on', 'cw-tip-nudge');
    void launcher.offsetWidth; // a reflow while it's hidden, so its CSS animations start over
    showCallout();
  }

  callout.querySelector('.cw-callout-cta')?.addEventListener('click', () => safeGuard(openPanel));
  callout.querySelector('.cw-callout-close')?.addEventListener('click', () => {
    safeGuard(() => {
      closeCallout();
      bubble.focus(); // the button that held focus is gone; the launcher is the natural next stop
    });
  });

  let revealed = false;
  function revealLauncher() {
    if (revealed || disabled) return;
    revealed = true;
    launcher.style.display = '';
    applyLauncherPos(); // now that it has a size to clamp against
    if (calloutWanted) scheduleCallout();
    else scheduleNudge();
    playLauncherVideo(); // a play() issued while display:none can be dropped by some browsers
  }
  const revealFallback = setTimeout(() => safeGuard(revealLauncher), REVEAL_TIMEOUT_MS);
  void refreshSession().then((s) => {
    safeGuard(() => {
      clearTimeout(revealFallback);
      if (s.failure?.permanent) {
        // Opened already — ChatWidget.open() on load, or a click during the
        // reveal fallback — the panel would stay on screen with no launcher
        // left to close or reopen it from.
        if (isOpen) safeGuard(closePanel);
        disabled = true;
        launcher.style.display = 'none';
        launcherVideo?.pause(); // never shown, so nothing should keep decoding
        return;
      }
      revealLauncher();
      if (reopenAfterNavigate) openPanel();
    });
  });

  const api: ChatWidgetPublicApi = {
    open: () => safeGuard(openPanel),
    close: () => safeGuard(closePanel),
    toggle: () => safeGuard(() => (isOpen && !isMinimized ? closePanel() : openPanel())),
    invite: () => safeGuard(replayCallout),
  };

  (window as unknown as { ChatWidget: ChatWidgetPublicApi }).ChatWidget = api;
}

safeGuard(init);
